import { ApiResponse } from "@payment-client/interfaces/Api";
import { CREDIT_TYPE, GetAllCardsResponse, GetCardDetailsResponse } from "@payment-client/interfaces/CardApi";
import { unwrapResponse, makeApiCall, makeDownstreamApiCall } from "@payment-client/utils/Api";
import { ICardOffer, IRewardOfferNewCard } from "@swiggy-private/payment-ui";
import { isBusinessLineSwiggyMoney, isOriginalBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { Storage } from "@swiggy-private/core-ui";
import { GIFT_DETAILS_STORAGE_KEY } from "@payment-client/constants/StorageKeys";
import { isEmpty } from "@swiggy-private/js-utils";
import { DOWNSTREAM_ENDPOINTS } from "@payment-client/constants/Hosts";
import { getSplitpayMethodsNameAsArray } from "@payment-client/helpers/SplitPay";

export interface GetSodexoBalanceResponse {
    cardToken: string;
    cardReference: string;
    cardBalance: number | string;
}

interface UpdateCardDetailPayload {
    cardRef: string;
    customerId: string;
    nameOnCard?: string;
    nickName: string;
}

export const ENDPOINT = {
    GET_SODEXO_BALANCE: "/api/v1/get-sodexo-balance",
    GET_ALL_CARDS: "/api/v1/get-all-cards",
    GET_CARD_DETAILS: "/api/v1/get-card-details",
    GET_CARD_OFFER: "/api/v1/get-card-offer",
    DELETE_CARD: "/api/v1/delete-card",
    UPDATE_CARD: "/api/v1/update-card",
};

export async function getSodexoBalance(apiHost: string): Promise<GetSodexoBalanceResponse[]> {
    const response = unwrapResponse(
        await makeApiCall<GetSodexoBalanceResponse[]>({
            apiHost,
            endpoint: ENDPOINT.GET_SODEXO_BALANCE,
            options: {},
        }),
    );

    return response.unwrapOrElse(() => []);
}

export async function getAllCards(apiHost: string): Promise<GetAllCardsResponse> {
    const response = unwrapResponse(
        await makeApiCall<GetAllCardsResponse>({
            apiHost,
            endpoint: ENDPOINT.GET_ALL_CARDS,
            options: {
                headers: {
                    source: "account_page",
                },
            },
        }),
    );

    return response.toPromise();
}

export async function getCardDetails(apiHost: string, cardBin: string): Promise<GetCardDetailsResponse | null> {
    let workflow = "";

    if (isBusinessLineSwiggyMoney() || isOriginalBusinessLineSwiggyMoney()) {
        const giftDetails = Storage.getItem(GIFT_DETAILS_STORAGE_KEY, null);
        if (!isEmpty(giftDetails)) {
            workflow = CREDIT_TYPE.VOUCHER;
        } else {
            workflow = CREDIT_TYPE.SELFLOAD;
        }
    }

    const searchParams: { cardBin: string; workflow: string } = { cardBin, workflow };

    const response = unwrapResponse(
        await makeApiCall<GetCardDetailsResponse | null>({
            apiHost,
            endpoint: ENDPOINT.GET_CARD_DETAILS,
            searchParams,
            options: {},
        }),
    );
    return response.unwrapOrElse(() => {
        return null;
    });
}

export async function getCardOffer(
    apiHost: string,
    cardBin: string,
    withoutSplitPay: string,
): Promise<ICardOffer | null> {
    const response = unwrapResponse(
        await makeApiCall<ICardOffer | null>({
            apiHost,
            endpoint: ENDPOINT.GET_CARD_OFFER,
            bodyParams: { cardBin, withoutSplitPay },
            options: {
                method: "POST",
            },
        }),
    );
    return response.unwrapOrElse(() => {
        return null;
    });
}

// Added for new flow for reward
export async function checkOfferAndRewardOnNewCardAPI(
    apiHost: string,
    cardBin: string,
    splitpayMethods: string,
): Promise<ApiResponse<IRewardOfferNewCard>> {
    const _splitpayMethods = Array.from(new Set(getSplitpayMethodsNameAsArray(splitpayMethods)));
    const response = await makeDownstreamApiCall<ApiResponse<IRewardOfferNewCard>>({
        apiHost,
        endpoint: DOWNSTREAM_ENDPOINTS.CHECK_OFFER_AND_REWARD_ON_NEW_CARD,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        bodyParams: { card_bin: cardBin, split_pay_methods: _splitpayMethods },
        options: {
            method: "POST",
        },
    });
    return response;
}

export async function deleteCard(apiHost: string, cardReference: string): Promise<ApiResponse<string>> {
    if (__DEV__) {
        return {
            statusCode: 0,
            statusMessage: "Card Deleted successfully",
            data: null,
        };
    }
    return await makeApiCall<string>({
        apiHost,
        endpoint: ENDPOINT.DELETE_CARD,
        bodyParams: { cardReference },
        options: {
            method: "POST",
        },
    });
}

export async function updateCard(apiHost: string, cardDetail: UpdateCardDetailPayload): Promise<ApiResponse> {
    if (__DEV__) {
        return {
            data: null,
            statusCode: 0,
            statusMessage: "Card Successfully updated",
        };
    }
    return await makeApiCall<ApiResponse>({
        apiHost,
        endpoint: ENDPOINT.UPDATE_CARD,
        bodyParams: { cardDetail },
        options: {
            method: "POST",
        },
    });
}
